import { useTerminalsStateRE } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { tenantDetailsColumns } from "@/Utils/tableColumns/tenantDetailsColumns";
import { trpc } from "@/Utils/trpc";
import { MediaQuery, useMediaQuery } from "@/Utils/useMediaQuery";
import { timezoneStore } from "@/api/hooks/useConfig";
import { useMinimalCustomers } from "@/api/hooks/useCustomers/useMinimalCustomers";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import LoadingTable from "@/components/LoadingTable";
import { Page } from "@/components/Page";
import { Table } from "@/components/Table";
import { Card } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import {
  OPTION_ALL,
  hasNonAllOptionActive,
} from "@/components/pageCards/filterSort/constants";
import {
  createTimeFilters,
  getTenantCategoryOptions,
} from "@/components/pageCards/filterSort/filterCreators";
import {
  TimeFilterCustomDate,
  getCustomTimeFrame,
  getTimeFrame,
} from "@/components/pageCards/filterSort/filterTimeFrame";
import {
  FilterSort,
  FilterSortMenuType,
} from "@/components/pageCards/filterSort/types";
import { AveragesListMobile } from "@/pages/CustomerDetails/components/AvarageListMobile";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { useCallback, useMemo, useState } from "react";

import moment from "moment-timezone";
import { FilterTimeFrameValue } from "server/config-api/index.js";

import ExpandedViewGrid from "./components/ExpandedViewGrid";
import { calcAverages, getTenantDetailsTableData } from "./functions";

export const TenantDetails = () => {
  const { currentTerminal } = useTerminalsStateRE();
  const { t } = useTrans();
  const { wasteTypeConfig, terminalConfig } = useConfigService();
  const matches = useMediaQuery(MediaQuery.ExtraSmall);

  const defaultPeriod = useMemo(
    () => terminalConfig?.defaultTimeFrame || FilterTimeFrameValue.Last30Days,
    [terminalConfig],
  );

  const { timezone } = timezoneStore();

  const [
    { timeframe: [selectedTimeframe] = [], tenantCategory },
    setActiveOptions,
  ] = useState<ActiveOptions<"timeframe" | "tenantCategory">>({} as any);

  const { startTimeISO, endTimeISO } = useMemo(() => {
    if (selectedTimeframe?.option === "interval.custom") {
      return getCustomTimeFrame(
        selectedTimeframe.value as TimeFilterCustomDate,
      );
    } else {
      return getTimeFrame(
        selectedTimeframe?.value as FilterTimeFrameValue,
        defaultPeriod,
      );
    }
  }, [selectedTimeframe, defaultPeriod]);

  // The start of the comparison period (an equal length period ending when the current period starts)
  const firstPeriodStartTimeISO = useMemo(() => {
    return moment(startTimeISO)
      .subtract(moment(endTimeISO).diff(moment(startTimeISO), "days"), "days")
      .tz(timezone, true)
      .toISOString();
  }, [startTimeISO, endTimeISO, timezone]);

  const { wasteTypes: sanityData } = useCommonEntitiesStore();
  const { usedTenantCategories } = useMinimalCustomers();

  const tenantCategoryValue = useMemo(() => {
    return hasNonAllOptionActive(tenantCategory)
      ? tenantCategory[0].value
      : undefined;
  }, [tenantCategory]);

  const { data: firstPeriod, isLoading: isLoadingFirstPeriod } =
    trpc.dwh.customers.get.useQuery(
      {
        startTimeISO: firstPeriodStartTimeISO,
        endTimeISO: startTimeISO,
        terminalId: currentTerminal.id,
      },
      {
        queryKeyHashFn: () =>
          [
            "customersFirstPeriod",
            startTimeISO,
            endTimeISO,
            currentTerminal.id,
          ].toString(),
      },
    );

  const { data: currentPeriod, isLoading: isLoadingCurrentPeriod } =
    trpc.dwh.customers.get.useQuery(
      {
        startTimeISO,
        endTimeISO,
        terminalId: currentTerminal.id,
      },
      {
        queryKeyHashFn: () =>
          [
            "customersCurrent",
            startTimeISO,
            endTimeISO,
            currentTerminal.id,
          ].toString(),
      },
    );

  const { data: commercialRealEstate, isLoading: isLoadingAggregate } =
    trpc.dwh.terminals.getById.useQuery(
      {
        ...getTimeFrame(FilterTimeFrameValue.Last30Days),
        terminalId: currentTerminal.id,
      },
      {
        queryKeyHashFn: () => ["terminal", currentTerminal.id].toString(),
      },
    );

  const { data: accessCodes, isLoading: isLoadingAccessCodes } =
    trpc.scanner.customers.getAllAccessCodes.useQuery(
      {
        terminalId: currentTerminal.id,
      },
      {
        queryKeyHashFn: () =>
          ["customersAccessCodes", currentTerminal.id].toString(),
      },
    );

  const averages = useMemo(
    () =>
      firstPeriod && currentPeriod && commercialRealEstate
        ? calcAverages(firstPeriod, currentPeriod, tenantCategoryValue)
        : {},
    [firstPeriod, currentPeriod, commercialRealEstate, tenantCategoryValue],
  );

  const tenantDetailsTableData = useMemo(
    () =>
      firstPeriod && currentPeriod && sanityData
        ? getTenantDetailsTableData({
            firstPeriod,
            currentPeriod,
            sanityData,
            wasteTypeConfig,
            tenantCategoryValue,
            accessCodes,
          })
        : [],
    [
      firstPeriod,
      currentPeriod,
      sanityData,
      wasteTypeConfig,
      tenantCategoryValue,
      accessCodes,
    ],
  );

  const isLoading =
    isLoadingFirstPeriod ||
    isLoadingCurrentPeriod ||
    isLoadingAggregate ||
    isLoadingAccessCodes;

  const filters: FilterSort[] = [
    createTimeFilters(
      {
        id: "timeframe",
        defaultValue: defaultPeriod,
        menuType: FilterSortMenuType.TimeFrame,
      },
      [
        FilterTimeFrameValue.Last30Days,
        FilterTimeFrameValue.Last6FullMonths,
        FilterTimeFrameValue.LastFullYear,
      ],
    ),
    {
      options: getTenantCategoryOptions(usedTenantCategories),
      title: "statisticsLabels:tenantCategory",
      id: "tenantCategory",
      type: "filter",
      menuType: FilterSortMenuType.Select,
      defaultValue: OPTION_ALL,
    },
  ];

  const getExpandedRowView = useCallback(
    (values: any) => (
      <ExpandedViewGrid
        tenantTableData={values}
        tenantsCurrent={currentPeriod?.customers || []}
      />
    ),
    [currentPeriod],
  );

  const averagesColumns = useMemo(
    () =>
      tenantDetailsColumns.map((el, i) => ({
        ...el,
        label: i === 0 ? "" : el.label,
      })),
    [],
  );

  const averagesData = useMemo(
    () => [{ ...averages, name: t("statisticsLabels:average") }],
    [averages, t],
  );

  return (
    <Page title="drawerLabels:tenantDetails" fullHeight>
      <Card
        filters={filters}
        onFilterOptionChange={setActiveOptions}
        overflow="auto"
        hasXContentPadding={false}
        hasYContentPadding={false}
      >
        {!tenantDetailsTableData.length && !isLoading ? (
          <EmptyView type="noData" />
        ) : (
          <div className="mx-4 my-6 flex h-full flex-col sm:mx-8 sm:min-w-[900px]">
            <h6 className="font-dm-sans text-large mb-4 font-medium text-black">
              {t("statisticsLabels:overview")}
            </h6>
            {isLoading ? (
              <div className="mb-14">
                <LoadingTable rows={2} />
              </div>
            ) : matches ? (
              <AveragesListMobile
                rows={averagesColumns}
                data={averagesData}
                ignoreRowNames={["name"]}
              />
            ) : (
              <Table
                columns={averagesColumns}
                data={averagesData}
                sortable={false}
                headerClassName="justify-items-start pr-4"
                rowClassName="bg-selected-color bg-selected-color pl-6"
                rightAlign
              />
            )}

            <h6 className="font-dm-sans text-large mt-10 mb-1 font-medium text-black">
              {t("statisticsLabels:details")}
            </h6>

            {isLoading ? (
              <LoadingTable rows={20} />
            ) : (
              <Table
                columns={tenantDetailsColumns}
                data={tenantDetailsTableData}
                getExpandedRowView={getExpandedRowView}
                isCollapsible
                headerClassName="justify-items-start pl-6"
                rowClassName="pl-6"
                rightAlign
                isSmallScreen={matches}
              />
            )}
          </div>
        )}
      </Card>
    </Page>
  );
};

export default TenantDetails;
